'use client';

import type { Theme, ThemeProviderProps as MuiThemeProviderProps } from '@mui/material/styles';
import type {} from './extend-theme-types';
import type { ThemeOptions } from './types';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as ThemeVarsProvider } from '@mui/material/styles';

import { useTranslate } from 'src/locales';

import { useSettingsContext } from 'src/components/settings';

import { useAuthContext } from 'src/auth/hooks';

import { createTheme } from './create-theme';
import { Rtl } from './with-settings/right-to-left';

// ----------------------------------------------------------------------

export type ThemeProviderProps = Partial<MuiThemeProviderProps<Theme>> & {
  themeOverrides?: ThemeOptions;
};

export function ThemeProvider({
  themeOverrides,
  children,
  modeStorageKey,
  ...other
}: ThemeProviderProps) {
  const settings = useSettingsContext();
  const { currentLang } = useTranslate();
  const { user } = useAuthContext();

  // Dark mode is persisted by MUI's own useColorScheme() under
  // modeStorageKey — a separate storage bucket from the settings cookie
  // above, and previously device-wide regardless of which account was
  // logged in. Scope it per user too, same as settings; `key` forces
  // ThemeVarsProvider to remount (and re-read storage) once the user is
  // known so switching accounts actually switches the persisted mode.
  const scopedModeStorageKey =
    user?.id && modeStorageKey ? `${modeStorageKey}:${user.id}` : modeStorageKey;

  const theme = createTheme({
    settingsState: settings.state,
    localeComponents: currentLang?.systemValue,
    themeOverrides,
  });

  return (
    <ThemeVarsProvider
      key={scopedModeStorageKey}
      disableTransitionOnChange
      theme={theme}
      modeStorageKey={scopedModeStorageKey}
      {...other}
    >
      <CssBaseline />
      <Rtl direction={settings.state.direction}>{children}</Rtl>
    </ThemeVarsProvider>
  );
}
